import { Mail, Linkedin, MessageCircle, MapPin, Zap, ArrowRight, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import thunderFoxLogo from "/fox-logo.png";

const customStyles = `
  .footer-grid-bg {
    background-size: 30px 30px;
    background-image: 
      linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px);
  }
  .electric-line {
    background: linear-gradient(90deg, transparent, #06b6d4, #a855f7, transparent);
    background-size: 200% 100%;
    animation: flow 3s linear infinite;
  }
  @keyframes flow {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }
  .social-glow:hover {
    box-shadow: 0 0 20px currentColor;
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { 
      icon: MessageCircle, 
      href: "https://wa.me/+917339010167", 
      color: "text-green-400", 
      bg: "hover:bg-green-500/10 hover:border-green-500",
      label: "WhatsApp" 
    },
    { 
      icon: Linkedin, 
      href: "https://www.linkedin.com/in/thunder-fox-web-solutions", 
      color: "text-blue-400", 
      bg: "hover:bg-blue-500/10 hover:border-blue-500",
      label: "LinkedIn"
    },
    { 
      icon: Mail, 
      href: "mailto:thunderfoxweb@gmail.com", 
      color: "text-yellow-400", 
      bg: "hover:bg-yellow-500/10 hover:border-yellow-500",
      label: "Email"
    },
  ];

  return (
    <footer className="relative mt-20 bg-slate-950 text-white overflow-hidden border-t border-slate-800">
      <style>{customStyles}</style>

      {/* --- BACKGROUND AMBIENCE --- */}
      <div className="absolute inset-0 footer-grid-bg opacity-40 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-slate-950/90 to-transparent pointer-events-none" />
      
      {/* --- ELECTRIC TOP BORDER --- */}
      <div className="absolute top-0 left-0 right-0 h-[2px] electric-line shadow-[0_0_15px_rgba(6,182,212,0.5)]" />

      <div className="container mx-auto px-4 pt-16 pb-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          
          {/* 1. BRAND & IDENTITY (4 Columns) */}
          <div className="lg:col-span-5 space-y-6">
            <Link to="/" className="inline-flex items-center space-x-4 group">
              <div className="relative w-16 h-16 flex items-center justify-center bg-slate-900 rounded-2xl border border-white/10 group-hover:border-cyan-500/50 transition-all duration-500 group-hover:shadow-[0_0_30px_rgba(6,182,212,0.2)]">
                <img 
                  src={thunderFoxLogo} 
                  alt="Thunder Fox Logo" 
                  className="h-10 w-10 relative z-10 group-hover:scale-110 transition-transform duration-300" 
                />
                <div className="absolute inset-0 bg-cyan-500/10 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity" />
              </div>
              
              <div>
                <h1 className="text-2xl font-black tracking-tight text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-cyan-400 group-hover:to-purple-500 transition-all">
                  THUNDER FOX
                </h1>
                <p className="text-xs text-slate-400 font-mono tracking-[0.2em] uppercase">Web Solutions</p>
              </div>
            </Link>

            <p className="text-slate-400 text-base leading-relaxed max-w-md">
              Engineering high-voltage digital experiences. We empower startups and SMEs with lightning-fast, secure, and scalable web architecture.
            </p>

            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3 text-slate-400 group">
                <div className="p-2 rounded-lg bg-slate-900 border border-slate-800 group-hover:border-cyan-500/30 transition-colors">
                   <MapPin className="w-4 h-4 text-cyan-500" />
                </div>
                <span className="text-sm group-hover:text-cyan-400 transition-colors">Cuddalore, Tamil Nadu, India</span>
              </div>
              
              <div className="flex items-center gap-3 text-slate-400 group">
                <div className="p-2 rounded-lg bg-slate-900 border border-slate-800 group-hover:border-yellow-500/30 transition-colors">
                   <ShieldCheck className="w-4 h-4 text-yellow-500" />
                </div>
                <span className="text-sm font-semibold text-white group-hover:text-yellow-400 transition-colors">MSME Registered Entity</span>
              </div>
            </div>
          </div>

          {/* 2. NAVIGATION LINKS (3 Columns) */}
          <div className="lg:col-span-3">
            <h4 className="text-lg font-bold text-white mb-8 flex items-center gap-2">
              <Zap className="w-4 h-4 text-purple-500" />
              QUICK ACCESS
            </h4>
            <ul className="space-y-4">
              {[
                { name: "Home Base", path: "/" },
                { name: "About The Fox", path: "/about#thunder-fox-about" },
                { name: "Service Matrix", path: "/services#thunder-fox-services" },
                { name: "Contact HQ", path: "/contact#thunder-fox-contact" }
              ].map((link, idx) => (
                <li key={idx}>
                  <Link 
                    to={link.path} 
                    className="group flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
                  >
                    <ArrowRight className="w-3 h-3 text-cyan-500 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                    <span className="relative">
                      {link.name}
                      <span className="absolute left-0 -bottom-1 w-0 h-[1px] bg-cyan-500 group-hover:w-full transition-all duration-300" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 3. CONNECT / SOCIALS (4 Columns) */}
          <div className="lg:col-span-4">
            <h4 className="text-lg font-bold text-white mb-8 flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-500" />
              ESTABLISH UPLINK
            </h4>
            
            <div className="grid grid-cols-3 gap-4">
              {socialLinks.map((social, idx) => (
                <a
                  key={idx}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`
                    group relative flex flex-col items-center justify-center p-4 
                    bg-slate-900/50 border border-slate-800 rounded-xl 
                    transition-all duration-300 hover:-translate-y-1 ${social.bg}
                  `}
                >
                  <social.icon className={`w-6 h-6 ${social.color} mb-2 transition-transform group-hover:scale-110`} />
                  <span className="text-[10px] uppercase font-bold text-slate-500 group-hover:text-white transition-colors">
                    {social.label}
                  </span>
                  
                  {/* Hover Glow Effect */}
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity rounded-xl bg-current ${social.color}`} />
                </a>
              ))}
            </div>

            {/* Newsletter / CTA Mini Box */}
            <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 relative overflow-hidden group">
               <div className="absolute inset-0 bg-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
               <h5 className="text-sm font-bold text-white mb-2 relative z-10">Ready to start?</h5>
               <p className="text-xs text-slate-400 mb-4 relative z-10">Deploy your project with us today.</p>
               <Link to="/contact" className="text-xs font-bold text-cyan-400 flex items-center gap-1 hover:gap-2 transition-all relative z-10">
                  Initiate Sequence <ArrowRight className="w-3 h-3" />
               </Link>
            </div>
          </div>
        </div>

        {/* --- FOOTER BOTTOM --- */}
        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            © {currentYear} Thunder Fox Web Solutions.
          </p>
          
          <div className="flex items-center gap-6 text-xs text-slate-600 font-mono uppercase tracking-wider">
             <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                Systems Nominal
             </span>
             <span>Privacy Protocol</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
